import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Button from "@mui/material/Button";
import logo from "../images/thumbnails/default-team-logo.png";
import { matchTypesWithThumbnailLabel } from "../consts/defaultMatchTypes";
import { getLeagueVisuals } from "../consts/leagueVisuals";
import { waitForImages } from "../utils/waitForImages";
import "./Thumbnail.scss";

const Thumbnail = () => {
  const thumbnailRef = useRef(null);
  const [image, setImage] = useState(null);
  const [fontColor, setFontColor] = useState("FFCC99");
  const [isExporting, setIsExporting] = useState(false);
  const { matchDetail } = useSelector((store) => store.matchDetailReducer);

  useEffect(() => {
    const { thumbnailImage, thumbnailFontColor } = getLeagueVisuals(
      matchDetail?.league_abv
    );
    setImage(thumbnailImage);
    setFontColor(thumbnailFontColor);
  }, [matchDetail?.league_abv]);

  const exportThumbnail = async () => {
    const thumbnail = thumbnailRef.current;
    if (!thumbnail || !window.domtoimage) {
      alert("Thumbnail export is unavailable. Please refresh and try again.");
      return;
    }

    setIsExporting(true);

    try {
      await waitForImages(thumbnail);
      const blob = await window.domtoimage.toBlob(thumbnail, {
        bgcolor: "#000000",
        quality: 1,
      });
      const filename = matchDetail?.match_name
        ? `${matchDetail.match_name}.png`
        : "thumbnail.png";
      window.saveAs(blob, filename);
    } catch (error) {
      console.error(error);
      alert("Thumbnail export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const showMatchTypeLabel = matchTypesWithThumbnailLabel.includes(
    matchDetail?.match_type
  );
  const homeLogo = matchDetail?.home_team?.logo || logo;
  const awayLogo = matchDetail?.away_team?.logo || logo;

  return (
    <div className="thumbnail-wrapper">
      <div className="thumbnail-preview-scale">
        <div ref={thumbnailRef} className="thumbnail">
          {image ? (
            <img
              className="thumbnail-background"
              src={image}
              alt=""
              aria-hidden="true"
            />
          ) : null}
          <div className="thumbnail-content">
            <div className="thumbnail-match-name-wrapper">
              {!showMatchTypeLabel ? (
                <div className="thumbnail-match-name">
                  {matchDetail?.match_name}
                </div>
              ) : (
                <div className="thumbnail-match-name-labeled">
                  <span>{matchDetail?.match_type?.toUpperCase()}</span>
                  <span>{matchDetail?.match_name}</span>
                </div>
              )}
            </div>
            <div className="thumbnail-teams" style={{ color: `#${fontColor}` }}>
              <div className="thumbnail-home-team-logo-wrapper">
                <img
                  className="thumbnail-home-team-logo"
                  src={homeLogo}
                  alt="home-team-logo"
                />
              </div>
              <span className="thumbnail-home-team-name thumbnail-team-name">
                {matchDetail?.home_team?.name?.toUpperCase() ?? ""}
              </span>
              <div className="thumbnail-away-team-logo-wrapper">
                <img
                  className="thumbnail-away-team-logo"
                  src={awayLogo}
                  alt="away-team-logo"
                />
              </div>
              <span className="thumbnail-away-team-name thumbnail-team-name">
                {matchDetail?.away_team?.name?.toUpperCase() ?? ""}
              </span>
            </div>
          </div>
        </div>
      </div>
      <Button
        variant="outlined"
        sx={{ margin: "0.5rem" }}
        onClick={exportThumbnail}
        disabled={isExporting || !matchDetail}
      >
        {isExporting ? "Generating..." : "Download"}
      </Button>
    </div>
  );
};

export default Thumbnail;
